import { DownloadFileDescriptor, FileDescriptor, UploadMetadata } from '@types';

const SECRET_BYTE_LENGTH = 32; // 256-bit key

let sodiumPromise: Promise<any> | null = null;

async function getSodium(): Promise<any> {
  if (!sodiumPromise) {
    sodiumPromise = import('libsodium-wrappers').then(async (mod: any) => {
      const s = (mod as any).default ?? mod;
      await (s as any).ready;
      return s;
    });
  }
  return sodiumPromise;
}

async function ready(): Promise<any> {
  return getSodium();
}

export function generateSecret(): string {
  const bytes = new Uint8Array(SECRET_BYTE_LENGTH);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

function keyFromSecret(secret: string): Uint8Array {
  const key = base64UrlDecode(secret);
  if (key.length !== SECRET_BYTE_LENGTH) {
    throw new Error('Invalid secret length');
  }
  return key;
}

export function toFileDescriptor(file: File, id: string): FileDescriptor {
  return {
    id,
    name: file.name,
    size: file.size,
    type: file.type,
    lastModified: file.lastModified
  };
}

// Metadata encryption using XSalsa20-Poly1305 (secretbox)
export async function encryptMetadata(
  metadata: UploadMetadata,
  secret: string,
  uploadId: string
): Promise<string> {
  const sodium = await ready();
  const key = await deriveSubkey(keyFromSecret(secret), 'SAFMETAD', subkeyIdFromString(uploadId));
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  const plaintext = Uint8Array.from(sodium.from_string(JSON.stringify(metadata)));
  const ciphertext = sodium.crypto_secretbox_easy(plaintext, nonce, key);
  const packed = new Uint8Array(nonce.length + ciphertext.length);
  packed.set(nonce, 0);
  packed.set(ciphertext, nonce.length);
  return base64UrlEncode(packed);
}

export async function decryptMetadata(
  packedBase64: string,
  secret: string,
  uploadId: string
): Promise<UploadMetadata> {
  const sodium = await ready();
  const key = await deriveSubkey(keyFromSecret(secret), 'SAFMETAD', subkeyIdFromString(uploadId));
  const packed = base64UrlDecode(packedBase64);
  const nonceSize = sodium.crypto_secretbox_NONCEBYTES;
  if (packed.length < nonceSize + sodium.crypto_secretbox_MACBYTES) {
    throw new Error('Invalid metadata ciphertext');
  }
  const nonce = packed.slice(0, nonceSize);
  const cipher = packed.slice(nonceSize);
  const plaintext = sodium.crypto_secretbox_open_easy(cipher, nonce, key);
  if (!plaintext) {
    throw new Error('Invalid metadata authentication');
  }
  return JSON.parse(sodium.to_string(plaintext)) as UploadMetadata;
}

// File encryption using secretstream_xchacha20poly1305 with simple length-prefixed framing
export async function encryptFileStream(
  file: File,
  secret: string,
  ctx: { uploadId: string; fileId: string }
): Promise<ReadableStream<Uint8Array>> {
  const sodium = await ready();
  const key = await deriveSubkey(
    keyFromSecret(secret),
    'SAFFILE ',
    subkeyIdFromString(`${ctx.uploadId}:${ctx.fileId}`)
  );
  const reader = file.stream().getReader();
  const { state, header } = sodium.crypto_secretstream_xchacha20poly1305_init_push(key);

  return new ReadableStream<Uint8Array>({
    async start(controller) {
      controller.enqueue(header);
    },
    async pull(controller) {
      const { value, done } = await reader.read();
      if (done) {
        const adFinal = sodium.from_string(
          `SAF|${ctx.uploadId}|${ctx.fileId}|${(this as any)._chunkIndex ?? 0}`
        );
        const final = sodium.crypto_secretstream_xchacha20poly1305_push(
          state,
          new Uint8Array(0),
          adFinal,
          sodium.crypto_secretstream_xchacha20poly1305_TAG_FINAL
        );
        controller.enqueue(u32be(final.length));
        controller.enqueue(final);
        controller.close();
        return;
      }

      const ad = sodium.from_string(
        `SAF|${ctx.uploadId}|${ctx.fileId}|${(this as any)._chunkIndex ?? 0}`
      );
      const ct = sodium.crypto_secretstream_xchacha20poly1305_push(
        state,
        value,
        ad,
        sodium.crypto_secretstream_xchacha20poly1305_TAG_MESSAGE
      );
      controller.enqueue(u32be(ct.length));
      controller.enqueue(ct);
      (this as any)._chunkIndex = ((this as any)._chunkIndex ?? 0) + 1;
    },
    cancel(reason) {
      reader.cancel(reason).catch(() => {});
    }
  });
}

export async function decryptFileStream(
  stream: ReadableStream<Uint8Array>,
  secret: string,
  ctx: { uploadId: string; fileId: string }
): Promise<ReadableStream<Uint8Array>> {
  const sodium = await ready();
  const key = await deriveSubkey(
    keyFromSecret(secret),
    'SAFFILE ',
    subkeyIdFromString(`${ctx.uploadId}:${ctx.fileId}`)
  );
  const reader = stream.getReader();
  let buffer = new Uint8Array(0);

  async function readExact(n: number): Promise<Uint8Array> {
    while (buffer.length < n) {
      const { value, done } = await reader.read();
      if (done) break;
      if (value && value.length) {
        const combined = new Uint8Array(buffer.length + value.length);
        combined.set(buffer, 0);
        combined.set(value, buffer.length);
        buffer = combined;
      }
    }
    if (buffer.length < n) {
      throw new Error('Unexpected end of stream');
    }
    const out = buffer.slice(0, n);
    buffer = buffer.slice(n);
    return out;
  }

  return new ReadableStream<Uint8Array>({
    async start(controller) {
      // Read header
      const header = await readExact(sodium.crypto_secretstream_xchacha20poly1305_HEADERBYTES);
      const pullState = sodium.crypto_secretstream_xchacha20poly1305_init_pull(header, key);
      (this as any)._pullState = pullState;
    },
    async pull(controller) {
      const pullState = (this as any)._pullState as any;
      // Read next frame length
      const lenBytes = await readExact(4);
      const len = (lenBytes[0] << 24) | (lenBytes[1] << 16) | (lenBytes[2] << 8) | lenBytes[3];
      const cipher = await readExact(len);
      const ad = sodium.from_string(
        `SAF|${ctx.uploadId}|${ctx.fileId}|${(this as any)._chunkIndex ?? 0}`
      );
      const res = sodium.crypto_secretstream_xchacha20poly1305_pull(pullState, cipher, ad);
      if (!res) {
        throw new Error('Decryption failed');
      }
      const { message, tag } = res as { message: Uint8Array; tag: number };
      if (message.length) {
        controller.enqueue(message);
      }
      if (tag === sodium.crypto_secretstream_xchacha20poly1305_TAG_FINAL) {
        controller.close();
      }
      (this as any)._chunkIndex = ((this as any)._chunkIndex ?? 0) + 1;
    },
    cancel() {
      // Nothing else to release
    }
  });
}

export function descriptorsToDownloadable(files: FileDescriptor[]): DownloadFileDescriptor[] {
  return files.map(({ id, name, size, type }) => ({ id, name, size, type }));
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(s: string): Uint8Array {
  s = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = s.length % 4;
  if (pad) s += '='.repeat(4 - pad);
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function u32be(n: number): Uint8Array {
  return new Uint8Array([(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff]);
}

async function deriveSubkey(masterKey: Uint8Array, context8: string, id: number): Promise<Uint8Array> {
  const sodium = await ready();
  const ctxStr = context8.padEnd(8, ' ').slice(0, 8);
  
  // Ensure id is a valid unsigned 32-bit integer
  const unsignedId = id >>> 0;
  
  try {
    // Always use BigInt to avoid subkey_id warnings from libsodium
    const sub = sodium.crypto_kdf_derive_from_key(
      sodium.crypto_secretbox_KEYBYTES,
      BigInt(unsignedId) as any, // Type assertion to handle BigInt requirement
      ctxStr,
      masterKey
    );
    return sub;
  } catch (error) {
    console.error('[Crypto] Error in deriveSubkey:', error);
    console.error('[Crypto] Parameters used:', {
      keyBytes: sodium.crypto_secretbox_KEYBYTES,
      id: BigInt(unsignedId),
      context: ctxStr,
      masterKeyLength: masterKey.length
    });
    throw error;
  }
}

function subkeyIdFromString(s: string): number {
  // 32-bit FNV-1a hash; produces an unsigned 32-bit integer
  if (!s || typeof s !== 'string') {
    throw new Error('Invalid input for subkeyIdFromString: must be a non-empty string');
  }
  
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i) & 0xff;
    h = Math.imul(h, 16777619) >>> 0;
  }
  
  // Ensure we always return a positive unsigned 32-bit integer
  const result = h >>> 0;
  
  return result;
}
